import { useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { PageHeader, ResourceHeader, Tabs } from '@factorialco/f0-react/dist/experimental'
import { Upload } from '@factorialco/f0-react/icons/app'
import { PayrollCyclesDocument, DevFeaturesDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import PayrollCycleExportModal from 'modules/bookkeepers/components/PayrollCycle/ExportModal'
import {
  getPayrollRunStatusText,
  getPayrollRunStatusVariant,
} from 'modules/compensations/lib/payrollRunStatus'
import { useHeaderSyncAction } from 'modules/integrations/public'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

const PayrollCycleDetails = () => {
  const { headerSyncActions } = useHeaderSyncAction()
  const { id } = useParams()
  const { navigation } = useNavigation()
  const cycleId = Number(id)
  const [openExportModal, setOpenExportModal] = useState(false)
  const [fetchDevFeatures, { loading: devFeaturesLoading, data: devFeaturesData }] =
    useLazyQuery(DevFeaturesDocument)

  const tabs = [
    {
      id: 'documents',
      active: true,
      href: navigation.bookkeepers.clients.payrollCycles.details.documents.fullPath({
        id: id ?? '',
      }),
      label: i18n.t('bookkeepers.clients.payroll_cycles.documents.tab'),
    },
    {
      id: 'messages',
      href: navigation.bookkeepers.clients.payrollCycles.details.messages.fullPath({
        id: id ?? '',
      }),
      label: i18n.t('bookkeepers.clients.payroll_cycles.comments.tab'),
    },
  ]

  const { loading, data: cyclesData } = useQuery(PayrollCyclesDocument, {
    variables: {
      ids: [cycleId],
    },
    context: {
      isBookkeeperView: true,
    },
  })

  const companyId = cyclesData?.compensations.payrollRunsConnection.nodes?.[0]?.cycle?.company?.id
  useUnsafeEffect(() => {
    if (companyId) fetchDevFeatures({ variables: { companyIds: [companyId] } })
  }, [companyId, fetchDevFeatures])

  const handleCloseExportModal = () => {
    setOpenExportModal(false)
  }

  const handleOpenExportModal = () => {
    setOpenExportModal(true)
  }

  const payrollRun = cyclesData?.compensations.payrollRunsConnection.nodes?.[0]
  const isCompensationSyncInProfessionalsEnabled =
    devFeaturesData?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_enable_compensation_sync_in_professionals'
    )?.enabled

  if (loading) return <Loading />

  if (!payrollRun) {
    return null
  }

  return (
    <>
      <PageHeader
        module={{
          href: navigation.bookkeepers.clients.payrollCycles.fullPath(),
          id: 'compensations',
          name: i18n.t('bookkeepers.clients.payroll_cycles.title'),
        }}
        breadcrumbs={[
          {
            href: navigation.bookkeepers.clients.payrollCycles.details.fullPath({
              id: payrollRun.cycle?.id ?? '',
            }),
            id: 'cycles-details',
            label: payrollRun.cycle?.company?.name ?? '',
          },
        ]}
      />
      <ResourceHeader
        title={payrollRun.cycle?.company?.name ?? ''}
        status={{
          label: i18n.t('bookkeepers.clients.payroll_cycles.list_columns.status'),
          text: getPayrollRunStatusText(payrollRun.status ?? ''),
          variant: getPayrollRunStatusVariant(payrollRun.status ?? ''),
        }}
        metadata={[
          {
            label: i18n.t('bookkeepers.clients.payroll_cycles.list_columns.period'),
            value: {
              type: 'text',
              content: payrollRun.label ?? '',
            },
          },
        ]}
        secondaryActions={[
          {
            label: i18n.t('bookkeepers.clients.payroll_cycles.export'),
            onClick: handleOpenExportModal,
            icon: Upload,
            disabled: payrollRun.status === 'in_preparation',
            tooltip:
              payrollRun.status === 'in_preparation'
                ? i18n.t('bookkeepers.clients.payroll_cycles.export_tooltip')
                : undefined,
          },
          ...(isCompensationSyncInProfessionalsEnabled ? headerSyncActions : []),
        ]}
      />

      {openExportModal && (
        <PayrollCycleExportModal
          companyId={payrollRun.cycle?.company?.id}
          payrollRunId={payrollRun.id}
          countryCode={payrollRun.cycle?.country ?? ''}
          onClose={handleCloseExportModal}
        />
      )}
      <Tabs activeTabId={tabs.find((tab) => tab.active)?.id ?? ''} tabs={tabs} />
      <Outlet />
    </>
  )
}

export default PayrollCycleDetails
